import { useCallback, useState } from 'react';
import {
    enhancedScanApi,
    SessionTagDto,
    useSessionsControllerRetrieveSessionTagsQuery,
    useSessionsControllerUpdateSessionTagsMutation,
} from '@services/enhancedScanApi';
import SnackbarUtils from '@features/common/snackbar-utils';
import { selectIsBoddUser } from '@features/common/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';

const useSessionTagsSelector = () => {
    const isBoddUser = useAppSelector(selectIsBoddUser);
    const dispatch = useAppDispatch();

    const [selectedTagObjects, setSelectedTagObjects] = useState<SessionTagDto[]>([]);

    const {
        data: allAvailableTags = [],
        isLoading: isLoadingTags,
        isSuccess,
    } = useSessionsControllerRetrieveSessionTagsQuery({}, {
        skip: !isBoddUser,
    });
    const [updateSessionTags, { isLoading: isUpdatingTags }] = useSessionsControllerUpdateSessionTagsMutation();

    const setInitialTags = useCallback((tagIds: string[]) => {
        if (!isLoadingTags && isSuccess && allAvailableTags.length > 0) {
            const calculatedInitialTags = allAvailableTags.filter((tag) => tag.id && tagIds.includes(tag.id.toString()));
            setSelectedTagObjects(calculatedInitialTags);
        }
    }, [allAvailableTags, isLoadingTags, isSuccess]);

    const handleSaveSessionTags = useCallback(async (sessionId: number) => {
        if (!sessionId) {
            return;
        }
        const tagIds = selectedTagObjects
            .map((tag) => tag.id)
            .filter((id): id is number => Boolean(id));

        try {
            await updateSessionTags({
                sessionId,
                body: tagIds,
            }).unwrap();
            const successMsg = 'Session tags updated';
            SnackbarUtils.success(successMsg);

            dispatch(
                enhancedScanApi.util.updateQueryData(
                    'sessionsControllerRetrieveSession',
                    { id: sessionId },
                    (cachedItem) => {
                        cachedItem.sessionTags = selectedTagObjects;
                        return cachedItem;
                    },
                ),
            );
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : 'Failed to update session tags';
            SnackbarUtils.error(errorMessage);
        }
    }, [selectedTagObjects, updateSessionTags, dispatch]);

    const handleTagChange = useCallback((event: any, newValue: SessionTagDto[] | SessionTagDto | null) => {
        setSelectedTagObjects((newValue as SessionTagDto[]) || []);
    }, []);

    const getOptionLabel = useCallback((option: SessionTagDto) => option.label || '', []);

    // Return stub implementation for non-Bodd users
    if (!isBoddUser) {
        return {
            selectedTagObjects: [],
            allAvailableTags: [],
            handleTagChange: () => {
            },
            handleSaveSessionTags: async () => {
            },
            setInitialTags: () => {
            },
            getOptionLabel: () => '',
            isLoadingTags: false,
            isUpdatingTags: false,
            hasPermission: false,
            error: null,
            isValid: true,
        };
    }

    return {
        selectedTagObjects,
        allAvailableTags,
        handleTagChange,
        handleSaveSessionTags,
        getOptionLabel,
        isLoadingTags,
        isUpdatingTags,
        setInitialTags,
    };
};

export default useSessionTagsSelector;
